import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { tenantConfig } from '../config/tenant'
import Nav from '../components/Nav'
import Seo from '../components/Seo'
import Footer from '../components/Footer'
import { blogApi, type BlogPostSummary } from '../api/blogApi'

/**
 * BlogPage — index for the 165 migrated Greystone posts (D13).
 *
 * The old site dumped everything into one "Uncategorized" bucket; the migration
 * retags posts under four real categories. This index shows an "All" default
 * plus a tab per category, with a chronological (newest-first) post list.
 */

const CATEGORIES: { slug: string; label: string }[] = [
  { slug: 'home-loans', label: 'Home Loans' },
  { slug: 'refinancing', label: 'Refinancing' },
  { slug: 'business', label: 'Business' },
  { slug: 'market-updates', label: 'Market Updates' },
]

const CATEGORY_LABEL = new Map(CATEGORIES.map((c) => [c.slug, c.label]))

function formatDate(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

export default function BlogPage() {
  const [posts, setPosts] = useState<BlogPostSummary[]>([])
  const [total, setTotal] = useState<number>(0)
  const [active, setActive] = useState<string>('all')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    blogApi
      .listPosts()
      .then((data) => {
        if (cancelled) return
        setPosts(data.posts ?? [])
        setTotal(data.total ?? (data.posts ?? []).length)
      })
      .catch(() => {
        if (!cancelled) setError('We couldn’t load the latest posts. Please try again shortly.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Chronological, newest first (defensive — falls back to source order).
  const ordered = useMemo(() => {
    return [...posts].sort((a, b) => {
      const ta = a.published_at ? Date.parse(a.published_at) : 0
      const tb = b.published_at ? Date.parse(b.published_at) : 0
      return tb - ta
    })
  }, [posts])

  const visible = useMemo(
    () => (active === 'all' ? ordered : ordered.filter((p) => p.category === active)),
    [ordered, active],
  )

  const tabClass = (isActive: boolean) =>
    `px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
      isActive ? 'text-ink' : 'text-gs-text-soft border-[#E8E8E2] hover:bg-white'
    }`

  return (
    <>
      <Seo
        title="Blog"
        path="/blog"
        description="Mortgage tips, property insights and lending guides from our brokers."
      />
      <Nav />
      <main id="main" className="pt-36 pb-20">
        {/* HERO */}
        <section className="max-w-5xl mx-auto px-6 pb-10">
          <div className="text-xs font-semibold uppercase tracking-wider text-gs-text-muted mb-2">Insights</div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-ink font-semibold leading-tight mb-4">
            The {tenantConfig.name} blog.
          </h1>
          <p className="text-lg text-gs-text-soft max-w-3xl">
            Practical mortgage and lending guidance from our brokers, covering home loans,
            refinancing, business lending and the market movements that affect your rate.
          </p>
        </section>

        {/* CATEGORY TABS */}
        <section className="max-w-5xl mx-auto px-6 pb-8">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Blog categories">
            <button
              type="button"
              role="tab"
              aria-selected={active === 'all'}
              onClick={() => setActive('all')}
              className={tabClass(active === 'all')}
              style={active === 'all' ? { background: tenantConfig.accentColor } : undefined}
            >
              All posts
            </button>
            {CATEGORIES.map((c) => (
              <button
                key={c.slug}
                type="button"
                role="tab"
                aria-selected={active === c.slug}
                onClick={() => setActive(c.slug)}
                className={tabClass(active === c.slug)}
                style={active === c.slug ? { background: tenantConfig.accentColor } : undefined}
              >
                {c.label}
              </button>
            ))}
          </div>
        </section>

        {/* POST LIST */}
        <section className="max-w-5xl mx-auto px-6">
          {loading && <p className="text-gs-text-soft">Loading posts…</p>}
          {error && !loading && <p className="text-red-600">{error}</p>}

          {!loading && !error && visible.length === 0 && (
            <p className="text-gs-text-soft">No posts in this category yet.</p>
          )}

          <ul className="divide-y divide-[#E8E8E2] border-t border-[#E8E8E2]">
            {visible.map((p) => (
              <li key={p.slug}>
                <Link
                  to={`/blog/${p.slug}`}
                  className="group flex flex-col gap-1 py-6 md:flex-row md:items-center md:justify-between"
                >
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <span
                        className="text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full"
                        style={{ background: '#F4F4F0', color: '#4A4A44' }}
                      >
                        {CATEGORY_LABEL.get(p.category) ?? 'Insights'}
                      </span>
                      {p.published_at && (
                        <span className="text-xs text-gs-text-muted">{formatDate(p.published_at)}</span>
                      )}
                    </div>
                    <h2 className="font-heading text-xl text-ink group-hover:underline">{p.title}</h2>
                    {p.excerpt && <p className="text-sm text-gs-text-soft mt-1 max-w-2xl">{p.excerpt}</p>}
                  </div>
                  <span
                    className="text-sm font-semibold shrink-0 md:pl-6"
                    style={{ color: tenantConfig.primaryColor }}
                    aria-hidden="true"
                  >
                    Read →
                  </span>
                </Link>
              </li>
            ))}
          </ul>

          {!loading && !error && total > 0 && (
            <p className="text-xs text-gs-text-muted mt-8">
              Showing {visible.length} of {total} posts.
            </p>
          )}
        </section>
      </main>
      <Footer />
    </>
  )
}
